import { useMemo, useState, type FormEvent } from 'react';
import { fmt } from '@nyce/admin-kit';
import { Button, Chip, Dialog, IconButton, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import { X } from 'lucide-react';
import {
  ActionsRow,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Kpi,
  Loading,
  Page,
  TableScroller,
  Tabs,
  TagsField,
  useAsync,
  useInterval,
  useNotice,
} from '../../../components/common';
import { formatBytes, formatDuration } from '../../../lib/format';
import { useTargets } from '../../../identity/session';
import { useBuddyWrite } from '../../api/client';
import {
  knowledgeFor,
  type CustomerDocument,
  type DocSource,
  type DocSourceInput,
  type DocSourceKind,
  type DocSourceTest,
  type DocsStatus,
} from '../../api/knowledge';
import { CustomerDocuments } from './CustomerDocuments';
import { refOptions, toneOf, useLookup, useQueryState, when } from './shared';

/** What each kind calls its fields, so one form serves both. */
const KINDS: Record<
  DocSourceKind,
  {
    label: string;
    baseUrl: string;
    baseUrlHint: string;
    user: string;
    secret: string;
    scopes: string;
    scopesHint: string;
    hidden: string;
    hiddenHint: string;
  }
> = {
  confluence: {
    label: 'Confluence wiki',
    baseUrl: 'Confluence base URL',
    baseUrlHint: 'https://example.atlassian.net/wiki',
    user: 'E-mail (basic auth)',
    secret: 'API token',
    scopes: 'Space keys',
    scopesHint: 'Empty = every space the account can read',
    hidden: 'Include blog posts',
    hiddenHint: 'Blog posts as well as pages',
  },
  configtool: {
    label: 'Configuration tool',
    baseUrl: 'Configuration tool URL',
    baseUrlHint: 'https://config-tool.nycelogic.com',
    user: 'Service user',
    secret: 'Service password',
    scopes: 'Function groups',
    scopesHint: 'Empty = every group; the strategies are always indexed',
    hidden: 'Include hidden functions',
    hiddenHint: 'Functions the tool marks not visible are skipped by default',
  },
};
const kindOf = (k: string): DocSourceKind => (k === 'configtool' ? 'configtool' : 'confluence');
type Tab = 'sources' | 'search' | 'customer';

const emptySource = (): DocSourceInput => ({
  kind: 'confluence',
  name: '',
  baseUrl: '',
  email: '',
  apiToken: '',
  spaceKeys: [],
  includeBlogPosts: false,
  indexPdfs: false,
  refreshMinutes: 0,
  externalShare: { enabled: false, baseUrl: '', apiKey: '', spaceKeys: [], shareUuids: [] },
  settings: { authUrl: '', tenantId: '', language: 'ENG', linkTemplate: '' },
  enabled: true,
});

const toInput = (s: DocSource): DocSourceInput => ({
  kind: kindOf(s.kind),
  name: s.name,
  baseUrl: s.baseUrl,
  email: s.email ?? '',
  apiToken: '',
  spaceKeys: s.spaceKeys ?? [],
  includeBlogPosts: !!s.includeBlogPosts,
  indexPdfs: !!s.indexPdfs,
  refreshMinutes: s.refreshMinutes ?? 0,
  externalShare: {
    enabled: !!s.externalShare?.enabled,
    baseUrl: s.externalShare?.baseUrl ?? '',
    apiKey: '',
    spaceKeys: s.externalShare?.spaceKeys ?? [],
    shareUuids: s.externalShare?.shareUuids ?? [],
  },
  settings: {
    authUrl: s.settings?.authUrl ?? '',
    tenantId: s.settings?.tenantId ?? '',
    language: s.settings?.language ?? 'ENG',
    linkTemplate: s.settings?.linkTemplate ?? '',
  },
  enabled: s.enabled,
});

/** The body sent: empty secrets left out (= keep the stored ones), Confluence-only parts only for Confluence. */
export function sourceBody(v: DocSourceInput): DocSourceInput {
  const confluence = v.kind === 'confluence';
  return {
    ...v,
    apiToken: v.apiToken || undefined,
    indexPdfs: confluence && v.indexPdfs,
    externalShare:
      confluence && v.externalShare
        ? { ...v.externalShare, apiKey: v.externalShare.apiKey || undefined }
        : undefined,
    settings: confluence ? undefined : v.settings,
  };
}

export function sourceProblems(v: DocSourceInput, isNew: boolean): string[] {
  const p: string[] = [];
  if (!v.name.trim()) p.push('Name is required.');
  if (!/^https?:\/\/\S+$/.test(v.baseUrl.trim())) p.push(`${KINDS[v.kind].baseUrl}: an http(s) URL.`);
  if (isNew && !v.apiToken) p.push(`${KINDS[v.kind].secret} is required.`);
  if (v.refreshMinutes < 0) p.push('Refresh: 0 (manual) or more minutes.');
  if (v.kind === 'configtool') {
    if (!v.email.trim()) p.push('Service user is required.');
    if (!v.settings?.authUrl?.trim()) p.push('AUTH URL is required.');
    if (!v.settings?.tenantId?.trim()) p.push('AUTH tenant id is required.');
  }
  if (v.kind === 'confluence' && v.externalShare?.enabled && !v.externalShare.baseUrl?.trim())
    p.push('External Share base URL is required when it is on.');
  return p;
}

function TestResult({ result, title }: { result: DocSourceTest; title?: string }) {
  return (
    <div
      className={`bdk-test ${result.ok ? 'nyk-tone-green' : 'nyk-tone-red'}`}
      data-testid="docs-test-result"
    >
      <b>
        {title ? `${title}: ` : ''}
        {result.ok ? 'connection OK' : 'connection failed'}
      </b>
      {result.site && <span> · {result.site}</span>}
      {!result.ok && result.error && <div>{result.error}</div>}
      {result.ok && !!result.spaces?.length && (
        <ul className="bdk-spaces">
          {result.spaces.map((sp) => (
            <li key={sp.key}>
              <b>{sp.key}</b> {sp.name}
              {sp.pages != null && <span className="nyk-ui-hint"> · {fmt(sp.pages)} pages</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SourceDialog({
  target,
  source,
  onSaved,
  onClose,
}: {
  target: string;
  source: DocSource | null;
  onSaved: (s: DocSource) => void;
  onClose: () => void;
}) {
  const isNew = !source;
  const [v, setV] = useState<DocSourceInput>(() => (source ? toInput(source) : emptySource()));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<DocSourceTest | null>(null);
  const api = knowledgeFor(target).docs;
  const words = KINDS[v.kind];
  const share = v.externalShare ?? { enabled: false, spaceKeys: [], shareUuids: [] };
  const setShare = (patch: Partial<typeof share>) => setV({ ...v, externalShare: { ...share, ...patch } });
  const settings = v.settings ?? {};
  const setSettings = (patch: Partial<typeof settings>) =>
    setV({ ...v, settings: { ...settings, ...patch } });
  const problems = sourceProblems(v, isNew);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(source ? await api.update(source.id, sourceBody(v)) : await api.create(sourceBody(v)));
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  // The form as it stands, before saving: a saved source keeps its stored secrets for what is left empty.
  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      setTest(await api.test(source?.id ?? null, sourceBody(v)));
    } catch (e) {
      setTest({ ok: false, error: (e as Error).message });
    } finally {
      setTesting(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={isNew ? 'New documentation source' : `Edit ${source.name}`}
      maxWidth={780}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={() => void runTest()}
            loading={testing}
            disabled={problems.length > 0}
          >
            Test connection
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={problems.length > 0}>
            Save
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form" data-testid="source-form">
        <Select
          label="Kind"
          value={v.kind}
          disabled={!isNew}
          options={(Object.keys(KINDS) as DocSourceKind[]).map((k) => ({ value: k, label: KINDS[k].label }))}
          onChange={(kind) => setV({ ...v, kind: kind as DocSourceKind })}
        />
        <TextField label="Name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
        <TextField
          fieldClassName="nyk-span2"
          label={words.baseUrl}
          value={v.baseUrl}
          placeholder={words.baseUrlHint}
          onChange={(e) => setV({ ...v, baseUrl: e.target.value })}
        />
        <TextField
          label={words.user}
          value={v.email}
          autoComplete="off"
          onChange={(e) => setV({ ...v, email: e.target.value })}
        />
        <TextField
          label={words.secret}
          type="password"
          autoComplete="new-password"
          value={v.apiToken ?? ''}
          placeholder={source?.hasApiToken ? 'stored, leave empty to keep' : undefined}
          onChange={(e) => setV({ ...v, apiToken: e.target.value })}
        />
        {v.kind === 'configtool' && (
          <>
            <TextField
              label="AUTH URL"
              value={settings.authUrl ?? ''}
              placeholder="https://auth.nycelogic.com"
              onChange={(e) => setSettings({ authUrl: e.target.value })}
            />
            <TextField
              label="AUTH tenant id"
              value={settings.tenantId ?? ''}
              hint="The tenant whose scope claims carry config-tool:user"
              onChange={(e) => setSettings({ tenantId: e.target.value })}
            />
            <TextField
              label="Language"
              value={settings.language ?? ''}
              placeholder="ENG"
              hint="The NYCE language code the texts are read in"
              onChange={(e) => setSettings({ language: e.target.value })}
            />
            <TextField
              label="Link template (optional)"
              value={settings.linkTemplate ?? ''}
              hint="{id} = the page id; empty = answers name the page without linking it"
              onChange={(e) => setSettings({ linkTemplate: e.target.value })}
            />
          </>
        )}
        <TextField
          label="Refresh every (minutes)"
          type="number"
          min={0}
          value={String(v.refreshMinutes)}
          hint="0 = manual crawls only"
          onChange={(e) => setV({ ...v, refreshMinutes: Math.max(0, Number(e.target.value) || 0) })}
        />
        <div className="nyk-span2">
          <TagsField
            label={words.scopes}
            value={v.spaceKeys}
            onChange={(spaceKeys) => setV({ ...v, spaceKeys })}
            placeholder="Add one and press Enter"
            hint={words.scopesHint}
          />
        </div>
        <div className="nyk-ui-field nyk-switch-field">
          <span title={words.hiddenHint}>{words.hidden}</span>
          <Switch
            checked={v.includeBlogPosts}
            onChange={(includeBlogPosts) => setV({ ...v, includeBlogPosts })}
            label={v.includeBlogPosts ? 'On' : 'Off'}
          />
        </div>
        <div className="nyk-ui-field nyk-switch-field">
          <span>Enabled</span>
          <Switch
            checked={v.enabled}
            onChange={(enabled) => setV({ ...v, enabled })}
            label={v.enabled ? 'Crawled on schedule' : 'Kept, not crawled'}
          />
        </div>
        {v.kind === 'confluence' && (
          <fieldset className="nyk-fieldset" data-testid="external-share">
            <legend>External Share (public links)</legend>
            <div className="nyk-ui-field nyk-switch-field">
              <span>Enabled</span>
              <Switch
                checked={share.enabled}
                onChange={(enabled) => setShare({ enabled })}
                label={share.enabled ? 'Answers link the public page' : 'Off'}
              />
            </div>
            {share.enabled && (
              <>
                <TextField
                  label="External Share base URL"
                  value={share.baseUrl ?? ''}
                  onChange={(e) => setShare({ baseUrl: e.target.value })}
                />
                <TextField
                  label="API key"
                  type="password"
                  autoComplete="new-password"
                  value={share.apiKey ?? ''}
                  placeholder={source?.externalShare?.hasApiKey ? 'stored, leave empty to keep' : undefined}
                  onChange={(e) => setShare({ apiKey: e.target.value })}
                />
                <TagsField
                  label="Share space keys"
                  value={share.spaceKeys}
                  onChange={(spaceKeys) => setShare({ spaceKeys })}
                  placeholder="Space key"
                />
                <TagsField
                  label="Share uuids"
                  value={share.shareUuids}
                  onChange={(shareUuids) => setShare({ shareUuids })}
                  placeholder="Share uuid"
                  hint="Tree shares expand to their descendants"
                />
              </>
            )}
          </fieldset>
        )}
      </div>
      {problems.length > 0 && (
        <ul className="bdk-problems" data-testid="source-problems">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {test && <TestResult result={test} />}
    </Dialog>
  );
}

function CrawlSummary({ s }: { s: DocSource }) {
  const r = s.lastResult;
  if (!s.lastCrawlAt && !r) return <span className="nyk-ui-hint">never</span>;
  const errors = r?.errors ?? [];
  return (
    <div>
      {s.lastCrawlAt && <div>{when(s.lastCrawlAt)}</div>}
      {r && (
        <div className={r.error ? 'bdk-error' : 'nyk-ui-hint'}>
          {r.error
            ? `failed: ${r.error}`
            : [
                `${fmt(r.pages ?? 0)} pages`,
                `${fmt(r.skipped ?? 0)} unchanged`,
                `${fmt(r.chunks ?? 0)} chunks`,
                r.sharesUpdated != null ? `${fmt(r.sharesUpdated)} links` : null,
                r.durationMs != null ? formatDuration(r.durationMs) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
        </div>
      )}
      {errors.length > 0 && (
        <details className="bdk-details" data-testid={`errors-${s.id}`}>
          <summary className="bdk-error">
            {errors.length} error{errors.length === 1 ? '' : 's'}
          </summary>
          <pre className="bdk-pre">{errors.join('\n')}</pre>
        </details>
      )}
    </div>
  );
}

function SourcesTab({
  target,
  status,
  canWrite,
  reload,
  onEdit,
}: {
  target: string;
  status: DocsStatus;
  canWrite: boolean;
  reload: () => Promise<void>;
  onEdit: (s: DocSource) => void;
}) {
  const [full, setFull] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [test, setTest] = useState<{ source: DocSource; result: DocSourceTest } | null>(null);
  const notice = useNotice();
  const running = status.running ?? null;
  const api = knowledgeFor(target).docs;
  const act = async (id: string, fn: () => Promise<string | void>) => {
    setBusy(id);
    notice.clear();
    try {
      const done = await fn();
      await reload();
      if (done) notice.success(done);
    } catch (e) {
      notice.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="nyk-card">
      {notice.element}
      <div className="nyk-card-head">
        <h2>Sources</h2>
        {canWrite && (
          <label className="nyk-check">
            <input type="checkbox" checked={full} onChange={(e) => setFull(e.target.checked)} />
            <span>Full crawl (ignore content hashes)</span>
          </label>
        )}
      </div>
      {status.sources.length === 0 ? (
        <EmptyState
          title="No documentation sources"
          hint="Add a Confluence site or the Configuration Tool so the assistant can cite documentation."
        />
      ) : (
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="docs-sources">
            <thead>
              <tr>
                <th>Source</th>
                <th>Scopes</th>
                <th className="nyk-ui-num">Pages</th>
                <th className="nyk-ui-num">Chunks</th>
                <th className="nyk-ui-num">External links</th>
                <th>Last crawl</th>
                <th>Status</th>
              </tr>
            </thead>
            {status.sources.map((s) => {
              const state = running?.sourceId === s.id ? 'crawling…' : s.enabled ? 'enabled' : 'disabled';
              return (
                <tbody key={s.id} className="nyk-row-group" data-source={s.id}>
                  <tr>
                    <td className="nyk-card-title">
                      <b>{s.name}</b> <Chip size="sm">{KINDS[kindOf(s.kind)].label}</Chip>
                      <div className="nyk-ui-hint nyk-url">{s.baseUrl}</div>
                      {s.refreshMinutes > 0 && (
                        <div className="nyk-ui-hint">every {s.refreshMinutes} min</div>
                      )}
                    </td>
                    <td data-label={KINDS[kindOf(s.kind)].scopes}>
                      <div className="bdk-chips">
                        {s.spaceKeys?.length ? (
                          s.spaceKeys.map((k) => (
                            <Chip key={k} size="sm">
                              {k}
                            </Chip>
                          ))
                        ) : (
                          <span className="nyk-ui-hint">all</span>
                        )}
                      </div>
                      {s.includeBlogPosts && (
                        <div className="nyk-ui-hint">
                          + {kindOf(s.kind) === 'confluence' ? 'blog posts' : 'hidden functions'}
                        </div>
                      )}
                    </td>
                    <td className="nyk-ui-num" data-label="Pages">
                      {fmt(s.pages ?? 0)}
                    </td>
                    <td className="nyk-ui-num" data-label="Chunks">
                      {fmt(s.chunks ?? 0)}
                    </td>
                    <td className="nyk-ui-num" data-label="External links">
                      {s.externalShare?.enabled ? (
                        fmt(s.pagesWithExternalUrl ?? 0)
                      ) : (
                        <span className="nyk-ui-hint">off</span>
                      )}
                    </td>
                    <td data-label="Last crawl">
                      <CrawlSummary s={s} />
                    </td>
                    <td data-label="Status">
                      <Chip size="sm" className={toneOf(state)}>
                        {state}
                      </Chip>
                    </td>
                  </tr>
                  {canWrite && (
                    <ActionsRow colSpan={7}>
                      <Button size="sm" variant="ghost" onClick={() => onEdit(s)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === s.id}
                        onClick={() =>
                          void act(s.id, async () => setTest({ source: s, result: await api.test(s.id) }))
                        }
                      >
                        Test
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === s.id || !!running}
                        title={running ? 'A crawl is running' : undefined}
                        onClick={() =>
                          void act(s.id, async () => {
                            await api.crawl(s.id, full);
                            return `${full ? 'Full crawl' : 'Crawl'} of ${s.name} started`;
                          })
                        }
                      >
                        Crawl now
                      </Button>
                      {s.externalShare?.enabled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy === s.id}
                          onClick={() =>
                            void act(s.id, async () => {
                              const r = await api.syncShares(s.id);
                              return (
                                `External links of ${s.name}: ${r.updated} updated, ${r.cleared} cleared` +
                                (r.errors?.length
                                  ? `, ${r.errors.length} errors: ${r.errors.join('; ')}`
                                  : '')
                              );
                            })
                          }
                        >
                          Sync external links
                        </Button>
                      )}
                      <ConfirmButton
                        size="sm"
                        variant="danger"
                        disabled={busy === s.id}
                        message={`Delete the source ${s.name} and its ${fmt(s.pages ?? 0)} indexed pages?`}
                        onConfirm={() =>
                          void act(s.id, async () => {
                            await api.remove(s.id);
                            return `Deleted ${s.name}`;
                          })
                        }
                      >
                        Delete
                      </ConfirmButton>
                    </ActionsRow>
                  )}
                </tbody>
              );
            })}
          </table>
        </TableScroller>
      )}
      {test && (
        <div className="bdk-test-row" data-source-test={test.source.id}>
          <TestResult result={test.result} title={test.source.name} />
          <IconButton label="Dismiss" onClick={() => setTest(null)}>
            <X size={16} />
          </IconButton>
        </div>
      )}
    </div>
  );
}

/** What search_docs answers, optionally as one system would see it (its customer documents included). */
function SearchTab({ target, status }: { target: string; status: DocsStatus }) {
  const q = useQueryState();
  const query = q.get('q');
  const links = q.get('links') === 'external' ? 'external' : 'internal';
  const system = q.get('system');
  const kinds = q.get('kinds');
  const label = q.get('label');
  const [draft, setDraft] = useState(query);
  const [showText, setShowText] = useState(false);
  const systems = useLookup(target, 'systems');
  const result = useAsync(
    () =>
      query
        ? knowledgeFor(target).docs.search({
            q: query,
            links,
            systemId: system || undefined,
            kinds: kinds || undefined,
            label: label || undefined,
          })
        : Promise.resolve(null),
    [target, query, links, system, kinds, label]
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    q.set({ q: draft.trim() });
  };
  const r = result.data;
  return (
    <div className="nyk-card" data-testid="docs-search">
      <div className="nyk-card-head">
        <h2>Search the index</h2>
        <span className="nyk-ui-hint">What the search_docs tool answers (hybrid vector + full-text)</span>
      </div>
      <form className="bdk-search" onSubmit={submit}>
        <input
          className="nyk-ui-input"
          type="search"
          aria-label="Search query"
          placeholder="e.g. how are replenishment rules configured"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <Button type="submit" disabled={!draft.trim()} loading={result.loading && !!query}>
          Search
        </Button>
      </form>
      <div className="bd-toolbar bdk-toolbar">
        <Select
          aria-label="Links"
          value={links}
          options={[
            { value: 'internal', label: 'Internal links' },
            { value: 'external', label: 'External links' },
          ]}
          onChange={(x) => q.set({ links: x === 'internal' ? null : x })}
        />
        <Select
          aria-label="Corpus"
          value={kinds}
          options={[
            { value: '', label: 'Every corpus' },
            ...[...new Set([...(status.kinds ?? []), ...(kinds ? [kinds] : [])])].map((k) => ({
              value: k,
              label: k === 'configtool' ? 'Configuration tool' : k === 'confluence' ? 'Confluence' : k,
            })),
          ]}
          onChange={(x) => q.set({ kinds: x })}
        />
        <Select
          aria-label="Page type"
          value={label}
          options={[
            { value: '', label: 'Every page type' },
            { value: 'function', label: 'Functions' },
            { value: 'strategy', label: 'Strategies' },
          ]}
          onChange={(x) => q.set({ label: x })}
        />
        <Select
          aria-label="Search as"
          value={system}
          options={[
            { value: '', label: 'No system (product docs only)' },
            ...refOptions(systems.data),
            ...(system && !systems.data?.some((s) => s.id === system)
              ? [{ value: system, label: system }]
              : []),
          ]}
          onChange={(x) => q.set({ system: x })}
        />
      </div>
      <ErrorBox error={result.error} />
      {r && (
        <div data-testid="docs-search-results">
          {r.hits.length === 0 && <EmptyState title="No hits" />}
          {r.hits.map((h, i) => (
            <div key={`${h.url ?? h.title}-${i}`} className="bdk-hit" data-hit={i}>
              <div className="bdk-hit-head">
                {h.url ? (
                  <a className="nyk-link" href={h.url} target="_blank" rel="noreferrer">
                    {h.title}
                  </a>
                ) : (
                  <b>{h.title}</b>
                )}
                {h.source && <Chip size="sm">{h.source}</Chip>}
                {h.type && <Chip size="sm">{h.type}</Chip>}
                {h.score != null && <span className="nyk-ui-hint">score {h.score.toFixed(3)}</span>}
              </div>
              <div className="nyk-ui-hint nyk-url">
                {h.url || 'no link: the answer names this page instead'}
              </div>
              <div className="bdk-excerpt">{h.excerpt}</div>
            </div>
          ))}
          {r.text && (
            <div className="bdk-model-text">
              <Button size="sm" variant="ghost" onClick={() => setShowText(!showText)}>
                {showText ? 'Hide' : 'Show'} what the model sees
                {r.method ? ` (${r.method})` : ''}
              </Button>
              {showText && <pre className="bdk-pre">{r.text}</pre>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Customer documents per owner: pick an organization or a system, or see every owner that has documents. */
function CustomerTab({ target }: { target: string }) {
  const q = useQueryState();
  const [kind, id] = (q.get('owner') || ':').split(/:(.*)/s) as [string, string];
  const ownerKind = kind === 'system' ? 'system' : 'organization';
  const refs = useLookup(target, ownerKind === 'system' ? 'systems' : 'organizations');
  const orgs = useLookup(target, 'organizations');
  const systems = useLookup(target, 'systems');
  const all = useAsync<CustomerDocument[] | null>(
    () => (id ? Promise.resolve(null) : knowledgeFor(target).customerDocs.list()),
    [target, id]
  );
  const owners = useMemo(() => {
    const m = new Map<
      string,
      { key: string; kind: string; id: string; docs: number; bytes: number; failed: number }
    >();
    for (const d of all.data ?? []) {
      const k = d.organizationId ? `organization:${d.organizationId}` : `system:${d.systemId}`;
      const o = m.get(k) ?? {
        key: k,
        kind: d.organizationId ? 'organization' : 'system',
        id: (d.organizationId ?? d.systemId)!,
        docs: 0,
        bytes: 0,
        failed: 0,
      };
      o.docs++;
      o.bytes += d.size;
      if (d.status === 'failed') o.failed++;
      m.set(k, o);
    }
    return [...m.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [all.data]);
  const nameOf = (k: string, i: string) =>
    ((k === 'system' ? systems.data : orgs.data) ?? []).find((r) => r.id === i)?.name ?? i;
  const options = refOptions(refs.data);
  return (
    <div className="nyk-card" data-testid="docs-customer">
      <div className="nyk-card-head">
        <h2>Customer documents</h2>
        <span className="nyk-ui-hint">A customer&apos;s own procedures, searched for its users only</span>
      </div>
      <div className="bd-toolbar bdk-toolbar">
        <Select
          aria-label="Owner kind"
          value={ownerKind}
          options={[
            { value: 'organization', label: 'Organization' },
            { value: 'system', label: 'System' },
          ]}
          onChange={(x) => q.set({ owner: `${x}:` })}
        />
        {options.length > 0 ? (
          <Select
            aria-label="Owner"
            value={id}
            placeholder={`Pick ${ownerKind === 'system' ? 'a system' : 'an organization'}`}
            options={
              id && !options.some((o) => o.value === id) ? [{ value: id, label: id }, ...options] : options
            }
            onChange={(x) => q.set({ owner: `${ownerKind}:${x}` })}
          />
        ) : (
          <input
            className="nyk-ui-input"
            aria-label="Owner"
            placeholder={`${ownerKind === 'system' ? 'System' : 'Organization'} id`}
            key={ownerKind}
            defaultValue={id}
            onBlur={(e) => q.set({ owner: `${ownerKind}:${e.target.value.trim()}` })}
          />
        )}
        {id && (
          <Button variant="ghost" onClick={() => q.set({ owner: null })}>
            All owners
          </Button>
        )}
      </div>
      {id ? (
        <CustomerDocuments
          key={`${ownerKind}:${id}`}
          target={target}
          organizationId={ownerKind === 'organization' ? id : undefined}
          systemId={ownerKind === 'system' ? id : undefined}
        />
      ) : (
        <>
          <ErrorBox error={all.error} />
          <Loading loading={all.loading && !all.data} />
          {all.data && owners.length === 0 && (
            <EmptyState title="No customer documents" hint="Pick an organization or system to upload some." />
          )}
          {owners.length > 0 && (
            <TableScroller>
              <table className="nyk-ui-table nyk-cards" data-testid="customer-doc-owners">
                <thead>
                  <tr>
                    <th>Owner</th>
                    <th className="nyk-ui-num">Documents</th>
                    <th className="nyk-ui-num">Size</th>
                    <th className="nyk-ui-num">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {owners.map((o) => (
                    <tr key={o.key} data-owner={o.key}>
                      <td className="nyk-card-title">
                        <button type="button" className="nyk-link" onClick={() => q.set({ owner: o.key })}>
                          {nameOf(o.kind, o.id)}
                        </button>
                        <div className="nyk-ui-hint">
                          {o.kind} {o.id}
                        </div>
                      </td>
                      <td className="nyk-ui-num" data-label="Documents">
                        {o.docs}
                      </td>
                      <td className="nyk-ui-num" data-label="Size">
                        {formatBytes(o.bytes)}
                      </td>
                      <td className={`nyk-ui-num${o.failed ? ' bdk-bad' : ''}`} data-label="Failed">
                        {o.failed}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          )}
        </>
      )}
    </div>
  );
}

/** Documentation index: status, sources with crawl controls, a search box to check the index, customer documents. */
export function BuddyDocsPage() {
  const { active } = useTargets();
  const canWrite = useBuddyWrite();
  const q = useQueryState();
  const tab = (['search', 'customer'].includes(q.get('tab')) ? q.get('tab') : 'sources') as Tab;
  const status = useAsync(() => knowledgeFor(active).docs.status(), [active]);
  const [editing, setEditing] = useState<{ source: DocSource | null } | null>(null);
  const notice = useNotice();
  const st = status.data;
  const running = st?.running ?? null;
  useInterval(() => void status.reload(), 5000, !!running);
  const reembed = async () => {
    try {
      await knowledgeFor(active).docs.reembed();
      notice.success('Re-embedding started in the background');
    } catch (e) {
      notice.error((e as Error).message);
    }
  };
  return (
    <Page
      title="Documentation"
      description="The documentation index behind search_docs: Confluence spaces and the Configuration Tool, crawled and embedded by nyce.buddy, plus each customer's own documents."
      actions={
        canWrite && st?.available ? (
          <>
            <ConfirmButton
              variant="ghost"
              message="Re-embed every chunk of every source? This runs in the background and calls the embedding model for the whole index."
              onConfirm={() => void reembed()}
            >
              Re-embed all
            </ConfirmButton>
            <Button onClick={() => setEditing({ source: null })}>New source</Button>
          </>
        ) : undefined
      }
    >
      {notice.element}
      <ErrorBox error={status.error} />
      <Loading loading={status.loading && !st} />
      {st && (
        <div className="nyk-kpis" data-testid="docs-status">
          <Kpi
            label="Index"
            value={
              <Chip size="sm" className={st.available ? 'nyk-tone-green' : 'nyk-tone-red'}>
                {st.available ? 'available' : 'unavailable'}
              </Chip>
            }
            sub={
              st.extension === false
                ? 'pgvector missing: create extension vector'
                : (st.error ?? 'pgvector installed')
            }
          />
          <Kpi label="Embedding model" value={st.embeddingModel ?? '—'} />
          <Kpi label="Pages" value={fmt(st.totals?.pages ?? 0)} />
          <Kpi label="Chunks" value={fmt(st.totals?.chunks ?? 0)} />
          <Kpi
            label="Crawl"
            value={running ? 'running' : 'idle'}
            sub={
              running
                ? `${st.sources.find((s) => s.id === running.sourceId)?.name ?? running.sourceId} · ${fmt(
                    running.pages ?? 0
                  )} pages since ${new Date(running.startedAt).toLocaleTimeString()}`
                : undefined
            }
          />
        </div>
      )}
      <Tabs
        value={tab}
        onChange={(t) => q.set({ tab: t === 'sources' ? null : t })}
        options={[
          { value: 'sources', label: 'Sources' },
          { value: 'search', label: 'Search test' },
          { value: 'customer', label: 'Customer documents' },
        ]}
      />
      {st && tab === 'sources' && (
        <SourcesTab
          target={active}
          status={st}
          canWrite={canWrite}
          reload={status.reload}
          onEdit={(source) => setEditing({ source })}
        />
      )}
      {st &&
        tab === 'search' &&
        (st.available ? (
          <SearchTab target={active} status={st} />
        ) : (
          <EmptyState
            card
            title="The index is unavailable"
            hint="Searching needs pgvector in buddy3's database."
          />
        ))}
      {tab === 'customer' && <CustomerTab target={active} />}
      {editing && (
        <SourceDialog
          target={active}
          source={editing.source}
          onSaved={(s) => {
            void status.reload();
            notice.success(`Saved ${s.name}`);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </Page>
  );
}
